import { useState, useEffect, useCallback } from "react";
import { X, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { MembershipBenefits } from "@/components/MembershipBenefits";
import CaptchaCheck from "@/components/checkout/CaptchaCheck";
import { toast } from "sonner";

const HIDE_ON = ["/login", "/join", "/register", "/dashboard", "/admin", "/checkout", "/contact", "/enhancers", "/memberships", "/wellness", "/marketplace", "/stewardship", "/offerings", "/booking-agreements", "/payment", "/gift-card-balance"];

export default function DiscoveryPopup() {
  const { user, joinDiscovery } = useAuth();
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [cap, setCap] = useState({});
  const [capKey, setCapKey] = useState(0);
  const [consent, setConsent] = useState(true);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const path = typeof window !== "undefined" ? window.location.pathname : "/";
  const suppressed =
    user || localStorage.getItem("eyw_discovery_dismissed") === "1" || HIDE_ON.some((p) => path.startsWith(p));

  const maybeShow = useCallback(() => {
    if (suppressed || dismissed || document.querySelector('[data-testid="booking-modal"]')) return;
    const scrolled = window.scrollY / (document.body.scrollHeight - window.innerHeight || 1);
    if (scrolled >= 0.35) setShow(true);
  }, [suppressed, dismissed]);

  useEffect(() => {
    if (suppressed) return;
    const timer = setTimeout(() => !dismissed && setShow(true), 25000);
    window.addEventListener("scroll", maybeShow, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", maybeShow);
    };
  }, [maybeShow, suppressed, dismissed]);

  const close = () => {
    setShow(false);
    setDismissed(true);
    localStorage.setItem("eyw_discovery_dismissed", "1");
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && show) close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [show]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await joinDiscovery({ email, password, first_name: firstName, last_name: lastName, phone, consent, accepted_terms: acceptedTerms, origin: window.location.origin, captcha_token: cap.token, captcha_answer: cap.answer, website: cap.website });
      setShow(false);
      toast.success("Welcome to Enhancing Your World — you're now a Discovery Member.");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(formatApiError(d?.message ? d.message : d) || err.message);
      setCapKey((k) => k + 1);
    } finally {
      setLoading(false);
    }
  };

  if (!show || suppressed) return null;

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center px-4 py-6 overflow-y-auto bg-black/40 backdrop-blur-sm" data-testid="discovery-popup">
      <div className="relative w-full max-w-md bg-[#F9F7F3] rounded-3xl shadow-2xl p-8 eyw-reveal">
        <button data-testid="discovery-popup-close" onClick={close} className="absolute top-4 right-4 text-[#4A5D6B] hover:text-[#144F75]">
          <X size={20} />
        </button>
        <img src="https://customer-assets-wrfwihn1.emergentagent.net/job_eyw-admin-suite/artifacts/ly5amm9a_EnhancingYourWorld2-02_compass57x57.png" alt="Enhancing Your World" className="h-12 w-12 rounded-full mb-4 object-contain" />
        <h2 className="font-serif text-2xl text-[#1A242B] leading-tight">Begin as a Discovery Member</h2>
        <p className="text-[#4A5D6B] text-sm mt-3">
          You do not have to know where to begin. Join as a Discovery Member and stay connected to upcoming
          events, teachings, community invitations, and holistic wellness offerings.
        </p>
        <MembershipBenefits plan="discovery" className="mt-4" />
        <form onSubmit={submit} className="mt-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="discovery-firstname" required placeholder="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)}
              className="w-full rounded-lg border border-[#E5E0D8] px-4 py-3 outline-none focus:border-[#144F75] bg-white" />
            <input data-testid="discovery-lastname" required placeholder="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)}
              className="w-full rounded-lg border border-[#E5E0D8] px-4 py-3 outline-none focus:border-[#144F75] bg-white" />
          </div>
          <input data-testid="discovery-email" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-lg border border-[#E5E0D8] px-4 py-3 outline-none focus:border-[#144F75] bg-white" />
          <input data-testid="discovery-phone" placeholder="Phone (optional)" value={phone} onChange={(e) => setPhone(e.target.value)}
            className="w-full rounded-lg border border-[#E5E0D8] px-4 py-3 outline-none focus:border-[#144F75] bg-white" />
            <input required type="password" minLength={12} autoComplete="new-password" placeholder="Create a password (12+ characters, letters & numbers)" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="discovery-password" className="w-full rounded-lg border border-[#E5E0D8] px-4 py-3 outline-none focus:border-[#144F75] bg-white" />
            <CaptchaCheck value={cap} onChange={setCap} refreshKey={capKey} testid="discovery-captcha" />
          <label className="flex items-start gap-2 text-xs text-[#4A5D6B]">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />
            I agree to receive community updates and understand I can unsubscribe at any time.
          </label>
          <label className="flex items-start gap-2 text-xs text-[#4A5D6B] cursor-pointer">
            <input required type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} data-testid="discovery-terms-checkbox" className="mt-0.5 accent-[#144F75]" />
            <span>I agree to the <a href="/terms" target="_blank" rel="noreferrer" className="text-[#144F75] underline">Terms of Service</a> and <a href="/privacy" target="_blank" rel="noreferrer" className="text-[#144F75] underline">Privacy Policy</a>.</span>
          </label>
          {error && <p className="text-sm text-red-600" data-testid="discovery-error">{error}</p>}
          <button data-testid="discovery-submit" type="submit" disabled={loading} className="eyw-btn w-full disabled:opacity-60">
            {loading && <Loader2 className="animate-spin" size={18} />} Join Free
          </button>
          <button type="button" onClick={close} className="w-full text-sm text-[#4A5D6B] hover:text-[#144F75] pt-1">Maybe Later</button>
        </form>
      </div>
    </div>
  );
}
